// firebase.js - Enhanced with PayPal payment helpers and atomic transactions
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider,
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence,
  sendPasswordResetEmail,
  signInWithCustomToken
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { increment } from 'firebase/firestore';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  // Required for Realtime Database (used by AuthContext.js for live
  // online/offline presence tracking). Add REACT_APP_FIREBASE_DATABASE_URL
  // to your .env — it looks like:
  //   https://<project-id>-default-rtdb.<region>.firebasedatabase.app
  // Find it in the Firebase console under Realtime Database → enable it
  // first if you haven't already (it's a separate product from Firestore,
  // not turned on by default). If this env var is missing, getDatabase()
  // below will throw, and AuthContext.js's presence effect already catches
  // that and disables itself silently — it will not break login/auth.
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
const auth = getAuth(app);
// Plain Firestore instance — no offline persistence layer.
//
// A persistentLocalCache (IndexedDB-backed) configuration was tried here
// to speed up repeat visits, but was reverted after it caused the entire
// app to load blank on at least one fresh/incognito browser with zero
// console errors — consistent with known Firestore SDK behavior where
// IndexedDB initialization issues in private-browsing contexts don't
// always surface as a catchable, loggable error before queries simply
// never resolve. Given that failure mode is silent and hard to detect
// remotely, the safer choice is the plain, well-understood getFirestore()
// every other part of this app was built and tested against. If offline
// persistence is revisited later, test thoroughly in actual incognito/
// private windows across Chrome, Safari, and Firefox before relying on it.
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);
// Realtime Database — used only for live presence (online/offline) in
// AuthContext.js / AdminDashboard.js's Users Management tab. Wrapped in a
// try/catch since getDatabase() throws if databaseURL is missing from the
// config above; presence is a nice-to-have and must never be able to break
// the rest of the app (auth, Firestore, payments, etc.) if RTDB isn't set
// up yet in the Firebase console.
//
// Also skipped entirely inside WeChat's in-app browser (and therefore
// inside the Mini Program's <web-view>). RTDB connects via dynamic
// subdomains like s-gke-usc1-nssi3-11.firebaseio.com that WeChat's
// business-domain whitelist cannot accommodate — the subdomain is
// randomized per-session and you can't verify domains you don't own.
// Every other Firebase service (Auth, Firestore, Storage, Functions)
// works fine without it, and the existing AuthContext code already
// null-checks `database` for the RTDB-not-configured case.
let database = null;
const _isWechatEnv = typeof navigator !== 'undefined' &&
  /micromessenger/i.test(navigator.userAgent);
if (_isWechatEnv) {
  console.info('[firebase] WeChat browser detected — skipping RTDB init (presence disabled)');
} else {
  try {
    database = getDatabase(app);
  } catch (err) {
    console.warn('Realtime Database not initialized (presence tracking will be disabled):', err.message);
  }
}

// Configure Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({
     prompt: 'select_account',
  include_granted_scopes: 'true',
  access_type: 'online',
  ux_mode: 'popup', // Explicitly set popup mode
  hl: 'en'
});

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768 && window.innerHeight <= 1024);
};

// ── WeChat login helpers ─────────────────────────────────────────────
// True when the page is open inside the WeChat in-app browser, where
// Google sign-in is blocked. Use this to show the WeChat login button.
export const isWechatBrowser = () => /micromessenger/i.test(navigator.userAgent);

// Build the WeChat OAuth2 web-authorization URL (公众号网页授权).
// redirect_uri MUST be on a domain configured as the 网页授权域名 in the
// WeChat Official Account console (i.e. gtamelb.com), so we use the current origin.
// Requires REACT_APP_WECHAT_APPID in your .env.
export const getWechatAuthUrl = (redirectPath = '/wechat-callback') => {
  const appid = process.env.REACT_APP_WECHAT_APPID;
  const redirectUri = encodeURIComponent(`${window.location.origin}${redirectPath}`);
  // random state for CSRF protection; verified on the callback page
  const state = Math.random().toString(36).slice(2);
  sessionStorage.setItem('wechat_oauth_state', state);
  return `https://open.weixin.qq.com/connect/oauth2/authorize` +
         `?appid=${appid}` +
         `&redirect_uri=${redirectUri}` +
         `&response_type=code` +
         `&scope=snsapi_userinfo` +
         `&state=${state}#wechat_redirect`;
};
// User profile helpers
export const createUserProfile = async (user, additionalData = {}) => {
  const { setDoc, doc } = await import('firebase/firestore');
  
  try {
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      name: user.displayName || additionalData.name || '',
      email: user.email,
      photoURL: user.photoURL || null,
      role: 'player',
      signupCount: 0,
      cancellationCount: 0,
      createdAt: new Date().toISOString(), // ← CHANGED: Use ISO string instead of serverTimestamp()
      updatedAt: new Date().toISOString(), // ← CHANGED: Use ISO string instead of serverTimestamp()
      emailVerified: user.emailVerified,
      provider: user.providerData[0]?.providerId || 'email',
      vaultedPaymentMethods: [],
      hasVaultedPayments: false,
      ...additionalData
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

// ENHANCED: Atomic payment processing with transaction
export const processPaymentWithTransaction = async (paymentData, eventData, userData) => {
  const { 
    runTransaction,
    doc,
    getDoc,
    addDoc,
    collection,
    arrayUnion,
    arrayRemove,
    increment,
    serverTimestamp
  } = await import('firebase/firestore');
  
  try {
    return await runTransaction(db, async (transaction) => {
      // Get fresh event data to prevent race conditions
      const eventRef = doc(db, 'events', eventData.id);
      const eventSnapshot = await transaction.get(eventRef);
      
      if (!eventSnapshot.exists()) {
        throw new Error('Event not found');
      }
      
      const currentEvent = eventSnapshot.data();
      const isWaitlistPromotion = paymentData.isWaitlistPromotion;
      const isGoingToWaitlist = !isWaitlistPromotion && 
                               (currentEvent.currentParticipants || 0) >= (currentEvent.maxParticipants || 0);
      
      // Create or update registration atomically
      if (paymentData.registrationId) {
        // Update existing registration (waitlist promotion)
        const registrationRef = doc(db, 'registrations', paymentData.registrationId);
        transaction.update(registrationRef, {
          status: 'active',
          paymentStatus: 'confirmed',
          paymentMethod: paymentData.paymentMethod,
          paymentId: paymentData.paymentId,
          orderId: paymentData.orderId,
          paymentDetails: paymentData.paymentDetails,
          totalCost: paymentData.totalCost,
          selectedOptions: paymentData.selectedOptions || [],
          confirmedAt: serverTimestamp(),
          paidAt: serverTimestamp(),
          vaultUsed: paymentData.vaultUsed || false,
          vaultId: paymentData.vaultId || null,
          paymentCaptureId: paymentData.paymentCaptureId || null,
          paymentAmount: paymentData.paymentAmount || paymentData.totalCost,
          paymentCurrency: paymentData.paymentCurrency || 'AUD',
          paymentCreateTime: paymentData.paymentCreateTime || serverTimestamp(),
          paymentUpdateTime: serverTimestamp()
        });

        // Move from waitlist to participants only if user is currently waitlisted
        if (currentEvent.waitlist?.includes(userData.uid)) {
          transaction.update(eventRef, {
            waitlist: arrayRemove(userData.uid),
            participants: arrayUnion(userData.uid),
            currentParticipants: increment(1)
          });
        }
      } else {
        // New registration - create new document
        const registrationRef = doc(collection(db, 'registrations'));
        const registrationData = {
          eventId: eventData.id,
          userId: userData.uid,
          userName: userData.name,
          userEmail: userData.email,
          userAvatar: userData.photoURL || null,
          selectedOptions: paymentData.selectedOptions || [],
          totalCost: paymentData.totalCost,
          paymentStatus: 'confirmed',
          paymentMethod: paymentData.paymentMethod,
          paymentId: paymentData.paymentId,
          orderId: paymentData.orderId,
          paymentDetails: paymentData.paymentDetails,
          paymentReference: paymentData.paymentReference,
          registeredAt: serverTimestamp(),
          paidAt: serverTimestamp(),
          checkedIn: false,
          status: isGoingToWaitlist ? 'waitlisted' : 'active',
          canCancel: true,
          cancelCount: 0,
          refundStatus: 'none',
          vaultUsed: paymentData.vaultUsed || false,
          vaultId: paymentData.vaultId || null,
          paymentCaptureId: paymentData.paymentCaptureId || null,
          paymentAmount: paymentData.paymentAmount || paymentData.totalCost,
          paymentCurrency: paymentData.paymentCurrency || 'AUD',
          paymentCreateTime: paymentData.paymentCreateTime || serverTimestamp(),
          paymentUpdateTime: serverTimestamp()
        };

        transaction.set(registrationRef, registrationData);

        // Update event participant count atomically
        if (!isGoingToWaitlist) {
          transaction.update(eventRef, {
            participants: arrayUnion(userData.uid),
            currentParticipants: increment(1)
          });
        } else {
          transaction.update(eventRef, {
            waitlist: arrayUnion(userData.uid)
          });
        }

        // Update user stats atomically
        const userRef = doc(db, 'users', userData.uid);
        transaction.update(userRef, {
          signupCount: increment(1),
          lastEventRegistration: serverTimestamp()
        });
      }

      return {
        success: true,
        isWaitlisted: isGoingToWaitlist,
        isWaitlistPromotion: isWaitlistPromotion,
        registrationId: paymentData.registrationId || 'new'
      };
    });
  } catch (error) {
    console.error('Payment transaction failed:', error);
    throw new Error(`Payment processing failed: ${error.message}`);
  }
};

// ENHANCED: Vault payment method management
export const savePaymentMethodToVault = async (userId, vaultData) => {
  const { updateDoc, doc, getDoc, serverTimestamp } = await import('firebase/firestore');
  
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data() || {};
    const existingMethods = userData.vaultedPaymentMethods || [];

    // Check if method already exists (by vault ID or card fingerprint)
    const existingMethod = existingMethods.find(method => 
      method.vaultId === vaultData.id ||
      (method.lastFour === vaultData.payment_source?.card?.last_digits && 
       method.paymentSource === vaultData.payment_source?.type)
    );

    if (!existingMethod) {
      const vaultInfo = {
        vaultId: vaultData.id,
        paymentSource: vaultData.payment_source?.type || 'paypal',
        lastFour: vaultData.payment_source?.card?.last_digits || null,
        brand: vaultData.payment_source?.card?.brand || null,
        expiryMonth: vaultData.payment_source?.card?.expiry?.month || null,
        expiryYear: vaultData.payment_source?.card?.expiry?.year || null,
        email: vaultData.payment_source?.paypal?.email_address || null,
        accountId: vaultData.payment_source?.paypal?.account_id || null,
        createdAt: serverTimestamp(),
        isDefault: existingMethods.length === 0,
        isActive: true,
        lastUsed: null
      };

      const updatedMethods = [...existingMethods, vaultInfo];
      
      await updateDoc(userRef, {
        vaultedPaymentMethods: updatedMethods,
        hasVaultedPayments: true,
        lastPaymentUpdate: serverTimestamp()
      });

      return { success: true, vaultInfo, message: 'Payment method saved successfully' };
    }

    return { success: false, message: 'Payment method already exists' };
  } catch (error) {
    console.error('Error saving payment method to vault:', error);
    throw new Error(`Failed to save payment method: ${error.message}`);
  }
};

// ENHANCED: Update vault payment method usage
export const updateVaultMethodUsage = async (userId, vaultId) => {
  const { updateDoc, doc, arrayUnion, serverTimestamp } = await import('firebase/firestore');
  
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      [`vaultedPaymentMethods.${vaultId}.lastUsed`]: serverTimestamp(),
      [`vaultedPaymentMethods.${vaultId}.usageCount`]: increment(1)
    });
  } catch (error) {
    console.error('Error updating vault method usage:', error);
  }
};

// ENHANCED: Remove vault payment method
export const removeVaultPaymentMethod = async (userId, vaultId) => {
  const { updateDoc, doc, getDoc } = await import('firebase/firestore');
  
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data() || {};
    const existingMethods = userData.vaultedPaymentMethods || [];

    const updatedMethods = existingMethods.filter(method => method.vaultId !== vaultId);
    
    await updateDoc(userRef, {
      vaultedPaymentMethods: updatedMethods,
      hasVaultedPayments: updatedMethods.length > 0
    });

    return { success: true, message: 'Payment method removed successfully' };
  } catch (error) {
    console.error('Error removing vault payment method:', error);
    throw new Error(`Failed to remove payment method: ${error.message}`);
  }
};

// PayPal payment helpers (existing functions enhanced)
export const createPaymentRecord = async (paymentData) => {
  const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
  
  try {
    const paymentRecord = {
      userId: paymentData.userId,
      eventId: paymentData.eventId,
      paymentId: paymentData.paymentId,
      orderId: paymentData.orderId,
      amount: paymentData.amount,
      currency: paymentData.currency || 'AUD',
      status: paymentData.status,
      paymentMethod: paymentData.paymentMethod || 'paypal',
      paymentDetails: paymentData.paymentDetails || {},
      selectedOptions: paymentData.selectedOptions || [],
      vaultUsed: paymentData.vaultUsed || false,
      vaultId: paymentData.vaultId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'payments'), paymentRecord);
    return { id: docRef.id, ...paymentRecord };
  } catch (error) {
    console.error('Error creating payment record:', error);
    throw error;
  }
};

export const updatePaymentStatus = async (paymentId, status, additionalData = {}) => {
  const { updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
  
  try {
    await updateDoc(doc(db, 'payments', paymentId), {
      status,
      updatedAt: serverTimestamp(),
      ...additionalData
    });
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
};

// Registration helpers with PayPal integration (enhanced)
export const createRegistrationWithPayment = async (registrationData, paymentData) => {
  const { 
    addDoc, 
    updateDoc, 
    doc, 
    collection, 
    arrayUnion, 
    increment,
    runTransaction,
    serverTimestamp
  } = await import('firebase/firestore');
  
  try {
    return await runTransaction(db, async (transaction) => {
      // Create registration record
      const registrationRef = doc(collection(db, 'registrations'));
      const registration = {
        eventId: registrationData.eventId,
        userId: registrationData.userId,
        userName: registrationData.userName,
        userEmail: registrationData.userEmail,
        userAvatar: registrationData.userAvatar,
        selectedOptions: registrationData.selectedOptions || [],
        totalCost: registrationData.totalCost,
        paymentStatus: 'confirmed',
        paymentMethod: paymentData.paymentMethod || 'paypal',
        paymentId: paymentData.paymentId,
        orderId: paymentData.orderId,
        paymentDetails: paymentData.paymentDetails,
        paymentReference: paymentData.paymentReference,
        registeredAt: serverTimestamp(),
        paidAt: serverTimestamp(),
        checkedIn: false,
        status: registrationData.isWaitlisted ? 'waitlisted' : 'active',
        canCancel: true,
        cancelCount: 0,
        refundStatus: 'none',
        vaultUsed: paymentData.vaultUsed || false,
        vaultId: paymentData.vaultId || null
      };

      transaction.set(registrationRef, registration);

      // Update event
      const eventRef = doc(db, 'events', registrationData.eventId);
      if (!registrationData.isWaitlisted) {
        transaction.update(eventRef, {
          participants: arrayUnion(registrationData.userId),
          currentParticipants: increment(1)
        });
      } else {
        transaction.update(eventRef, {
          waitlist: arrayUnion(registrationData.userId)
        });
      }

      // Update user stats
      const userRef = doc(db, 'users', registrationData.userId);
      transaction.update(userRef, {
        signupCount: increment(1)
      });

      return {
        registrationId: registrationRef.id,
        isWaitlisted: registrationData.isWaitlisted,
        ...registration
      };
    });
  } catch (error) {
    console.error('Error creating registration with payment:', error);
    throw error;
  }
};

// ENHANCED: Refund request helpers
export const createRefundRequest = async (registrationId, refundData) => {
  const { addDoc, updateDoc, doc, collection, serverTimestamp } = await import('firebase/firestore');
  
  try {
    // Create refund request record
    const refundRequest = {
      registrationId,
      userId: refundData.userId,
      eventId: refundData.eventId,
      originalPaymentId: refundData.paymentId,
      refundAmount: refundData.amount,
      currency: refundData.currency || 'AUD',
      reason: refundData.reason || '',
      status: 'requested',
      requestedAt: serverTimestamp(),
      processedAt: null,
      processedBy: null,
      refundType: refundData.refundType || 'full',
      originalAmount: refundData.originalAmount || refundData.amount
    };

    const docRef = await addDoc(collection(db, 'refunds'), refundRequest);

    // Update registration status
    await updateDoc(doc(db, 'registrations', registrationId), {
      refundStatus: 'requested',
      refundRequestedAt: serverTimestamp(),
      refundReason: refundData.reason || '',
      refundAmount: refundData.amount
    });

    return { id: docRef.id, ...refundRequest };
  } catch (error) {
    console.error('Error creating refund request:', error);
    throw error;
  }
};

export const updateRefundStatus = async (refundId, status, processedBy = null, additionalData = {}) => {
  const { updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
  
  try {
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
      ...additionalData
    };

    if (status === 'approved' || status === 'rejected' || status === 'completed') {
      updateData.processedAt = serverTimestamp();
      updateData.processedBy = processedBy;
    }

    await updateDoc(doc(db, 'refunds', refundId), updateData);
    return { success: true };
  } catch (error) {
    console.error('Error updating refund status:', error);
    throw error;
  }
};

// Event helpers
export const incrementEventViews = async (eventId) => {
  const { updateDoc, doc, increment, serverTimestamp } = await import('firebase/firestore');
  
  try {
    await updateDoc(doc(db, 'events', eventId), {
      viewCount: increment(1),
      lastViewedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error incrementing event views:', error);
  }
};

// Check-in helpers
export const updateCheckinStatus = async (registrationId, checkinData) => {
  const { updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
  
  try {
    await updateDoc(doc(db, 'registrations', registrationId), {
      checkedIn: true,
      checkedInAt: serverTimestamp(),
      checkedInLocation: checkinData.location || null,
      distanceToVenue: checkinData.distance || null,
      checkedInMethod: checkinData.method || 'manual'
    });

    // Also update user check-in count
    await updateDoc(doc(db, 'users', checkinData.userId), {
      checkinCount: increment(1),
      lastCheckin: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating check-in status:', error);
    throw error;
  }
};

// Notification helpers
export const createNotification = async (userId, notification) => {
  const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
  
  try {
    await addDoc(collection(db, 'notifications'), {
      userId,
      title: notification.title,
      message: notification.message,
      type: notification.type || 'info',
      read: false,
      urgent: notification.urgent || false,
      expiresAt: notification.expiresAt || null,
      createdAt: serverTimestamp(),
      data: notification.data || {}
    });
  } catch (error) {
    console.error('Error creating notification:', error);
  }
};

// Storage helpers (for user avatars, payment proofs, etc.)
export const uploadFile = async (file, path) => {
  const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
  
  try {
    const fileRef = ref(storage, path);
    const snapshot = await uploadBytes(fileRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Analytics helpers (if Firebase Analytics is enabled)
export const logEvent = (eventName, parameters = {}) => {
  if (process.env.NODE_ENV === 'production') {
    try {
      import('firebase/analytics').then(({ getAnalytics, logEvent: firebaseLogEvent }) => {
        const analytics = getAnalytics(app);
        firebaseLogEvent(analytics, eventName, parameters);
      });
    } catch (error) {
      console.log('Analytics not available');
    }
  }
};

// Payment analytics
export const logPaymentEvent = (eventName, paymentData) => {
  logEvent(eventName, {
    currency: paymentData.currency || 'AUD',
    value: paymentData.amount,
    payment_method: paymentData.paymentMethod || 'paypal',
    event_id: paymentData.eventId,
    vault_used: paymentData.vaultUsed || false
  });
};

// ENHANCED: Batch operations for better performance
export const batchUpdateParticipants = async (eventId, userUpdates) => {
  const { writeBatch, doc } = await import('firebase/firestore');
  
  try {
    const batch = writeBatch(db);
    
    userUpdates.forEach(update => {
      const userRef = doc(db, 'users', update.userId);
      batch.update(userRef, update.data);
    });

    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Error in batch update:', error);
    throw error;
  }
};

// Error handling helper
export const handleFirebaseError = (error) => {
  const errorMessages = {
    'auth/user-not-found': 'No account found with this email address',
    'auth/wrong-password': 'Incorrect password',
    'auth/email-already-in-use': 'An account with this email already exists',
    'auth/weak-password': 'Password should be at least 6 characters',
    'auth/invalid-email': 'Invalid email address',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later',
    'auth/popup-closed-by-user': 'Login was cancelled',
    'auth/popup-blocked': 'Popup was blocked. Please allow popups and try again',
    'permission-denied': 'You do not have permission to perform this action',
    'not-found': 'The requested resource was not found',
    'already-exists': 'The resource already exists',
    'unavailable': 'Service is currently unavailable. Please try again later',
    'aborted': 'Transaction was aborted due to a conflict',
    'failed-precondition': 'Operation failed due to invalid state'
  };

  return errorMessages[error.code] || error.message || 'An unexpected error occurred';
};

// Performance monitoring helper
export const trace = (traceName) => {
  if (process.env.NODE_ENV === 'production') {
    try {
      import('firebase/performance').then(({ getPerformance, trace: firebaseTrace }) => {
        const perf = getPerformance(app);
        return firebaseTrace(perf, traceName);
      });
    } catch (error) {
      console.log('Performance monitoring not available');
    }
  }
  return { start: () => {}, stop: () => {} };
};

// Export main services
export { 
 app,
  auth, 
  db, 
  storage, 
  functions,
  database,
  googleProvider,
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence,
  sendPasswordResetEmail,
  signInWithCustomToken 
};

// Export default for backward compatibility
export default app;